import { FC, useCallback, useMemo, useState } from "react";
import { useTolgee } from "@tolgee/react";
import { CircleFlagLanguage } from "react-circle-flags";
import { Select } from "../ui";
import { Option } from "../../types/select";
import cn from "../../utils/cn";
import { useAtomValue } from "jotai";
import { languageQuestionAtom } from "../../jotai-atoms";

interface LanguageSelectProps {
  className?: string;
  onLanguageChange?: (val: string) => void;
}

function getLanguage(options: Option[], language?: string) {
  return options.find((option) => option.value === language);
}

export const LanguageSelect: FC<LanguageSelectProps> = ({
  onLanguageChange,
  className,
}) => {
  const tolgee = useTolgee(["language"]);
  const languageQuestion = useAtomValue(languageQuestionAtom);
  const [language, setLanguage] = useState<Option | undefined>(undefined);
  const options = useMemo(() => {
    const languages = languageQuestion?.visibleChoices ?? [];
    if (languages && languages.length > 0) {
      return languages.map((lang: any) => ({
        value: lang.value,
        label: lang.text,
        Icon: (
          <CircleFlagLanguage
            languageCode={lang.value.split("-")[0]}
            width={16}
            height={16}
          />
        ),
      }));
    }
    return (
      tolgee.getInitialOptions().availableLanguages?.map((lang) => ({
        value: lang,
        label: `%-%tolgee:lang-${lang}%-%`,
        Icon: (
          <CircleFlagLanguage
            languageCode={lang.split("-")[0]}
            width={16}
            height={16}
          />
        ),
      })) ?? []
    );
  }, [languageQuestion, tolgee]);

  useMemo(() => {
    setLanguage(getLanguage(options, tolgee.getLanguage()));
  }, [options, tolgee]);

  const onValueChange = useCallback(
    (option: unknown) => {
      const newOption = option as Option;
      tolgee.changeLanguage(newOption.value).then(() => {
        if (onLanguageChange) onLanguageChange(newOption.value);
        setLanguage(getLanguage(options, newOption.value));
      });
    },
    [onLanguageChange, options, tolgee]
  );

  return (
    <Select
      className={cn("min-w-[150px] max-w-[150px]", className)}
      options={options}
      value={language}
      onChange={onValueChange}
      isSearchable={false}
      isClearable={false}
    />
  );
};
